import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Truck, Mail, Phone, MapPin, Facebook, Twitter, Instagram, Linkedin } from 'lucide-react';
import { publicAPI } from '@/services/api';

const FALLBACK_CONFIG = {
  platform_name: 'LogiConnect',
  platform_logo_url: null,
  contact_email: 'contact@logiconnect.gn',
  contact_phone: '',
  contact_address: 'Conakry, Guinée',
  facebook_url: '',
  twitter_url: '',
  instagram_url: '',
  linkedin_url: '',
};

const Footer = () => {
  const [config, setConfig] = useState(FALLBACK_CONFIG);

  useEffect(() => {
    publicAPI
      .getSiteConfig()
      .then((res) => {
        if (res.data) setConfig((prev) => ({ ...prev, ...res.data }));
      })
      .catch(() => {});
  }, []);

  const socials = [
    { url: config.facebook_url, icon: Facebook, label: 'Facebook' },
    { url: config.twitter_url, icon: Twitter, label: 'Twitter' },
    { url: config.instagram_url, icon: Instagram, label: 'Instagram' },
    { url: config.linkedin_url, icon: Linkedin, label: 'LinkedIn' },
  ].filter((s) => s.url);

  return (
    <footer className="bg-card border-t border-border">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Branding */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              {config.platform_logo_url ? (
                <img
                  src={config.platform_logo_url}
                  alt={config.platform_name}
                  className="h-10 w-auto object-contain"
                />
              ) : (
                <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center">
                  <Truck className="w-6 h-6 text-secondary-foreground" />
                </div>
              )}
              <span className="text-xl font-bold font-display text-foreground">
                {config.platform_name}
              </span>
            </div>
            <p className="text-muted-foreground text-sm">
              La plateforme de référence pour la logistique en Guinée. Connectons transporteurs et clients pour un avenir meilleur.
            </p>
          </div>

          {/* Liens rapides */}
          <div>
            <span className="font-semibold text-lg text-foreground mb-4 block">Liens rapides</span>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-muted-foreground hover:text-secondary transition-colors">
                  Accueil
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-muted-foreground hover:text-secondary transition-colors">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <span className="font-semibold text-lg text-foreground mb-4 block">Services</span>
            <ul className="space-y-2">
              {['Transport de marchandises', 'Transport de personnes', 'Suivi en temps réel'].map((s) => (
                <li key={s}>
                  <span className="text-muted-foreground text-sm">{s}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <span className="font-semibold text-lg text-foreground mb-4 block">Contact</span>
            <ul className="space-y-3">
              {config.contact_address && (
                <li className="flex items-start space-x-2">
                  <MapPin className="w-5 h-5 text-secondary mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground text-sm">{config.contact_address}</span>
                </li>
              )}
              {config.contact_phone && (
                <li className="flex items-center space-x-2">
                  <Phone className="w-5 h-5 text-secondary flex-shrink-0" />
                  <a href={`tel:${config.contact_phone}`} className="text-muted-foreground text-sm hover:text-secondary transition-colors">
                    {config.contact_phone}
                  </a>
                </li>
              )}
              {config.contact_email && (
                <li className="flex items-center space-x-2">
                  <Mail className="w-5 h-5 text-secondary flex-shrink-0" />
                  <a href={`mailto:${config.contact_email}`} className="text-muted-foreground text-sm hover:text-secondary transition-colors">
                    {config.contact_email}
                  </a>
                </li>
              )}
            </ul>
            {socials.length > 0 && (
              <div className="flex space-x-4 mt-4">
                {socials.map((s) => (
                  <a
                    key={s.label}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    className="text-muted-foreground hover:text-secondary transition-colors"
                  >
                    <s.icon className="w-5 h-5" />
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-border mt-8 pt-8 text-center">
          <p className="text-muted-foreground text-sm">
            &copy; {new Date().getFullYear()} {config.platform_name}. Tous droits réservés.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
